import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { splitItem } from "../../vendor/src/features/remotion-editor/editor/state/actions/split-item";
import { editableTree } from "./group-split";

export type TimeRange = { start: number; end: number };
export type RippleResult =
  | { ok: true; state: EditorState; replacements: Record<string, string[]> }
  | { ok: false; state: EditorState; reason: string };

export function mergeTimeRanges(ranges: readonly TimeRange[]): TimeRange[] {
  const merged: TimeRange[] = [];
  for (const range of [...ranges].sort((a, b) => a.start - b.start)) {
    if (
      !Number.isInteger(range.start) ||
      !Number.isInteger(range.end) ||
      range.start < 0 ||
      range.end <= range.start
    )
      throw new Error("invalid-time-range");
    const previous = merged.at(-1);
    if (previous && range.start <= previous.end)
      previous.end = Math.max(previous.end, range.end);
    else merged.push({ ...range });
  }
  return merged;
}

/** Remove time once from each requested unlocked track. Every split remains a source window, not new media. */
export function removeTimelineTime(
  state: EditorState,
  trackIds: readonly string[],
  ranges: readonly TimeRange[],
): RippleResult {
  const fail = (reason: string): RippleResult => ({ ok: false, state, reason });
  if (!Number.isFinite(state.undoableState.fps) || state.undoableState.fps <= 0)
    return fail("invalid-fps");
  let merged: TimeRange[];
  try {
    merged = mergeTimeRanges(ranges);
  } catch {
    return fail("invalid-time-range");
  }
  if (
    trackIds.some(
      (id) => !state.undoableState.tracks.some((track) => track.id === id),
    )
  )
    return fail("missing-track");
  const targetTracks = new Set(
    state.undoableState.tracks
      .filter((track) => trackIds.includes(track.id) && !track.locked)
      .map((track) => track.id),
  );
  const initialRoots = state.undoableState.tracks.flatMap(
    (track) => track.items,
  );
  const origins = new Map(initialRoots.map((id) => [id, [id]]));
  if (!merged.length || !targetTracks.size)
    return {
      ok: true,
      state,
      replacements: Object.fromEntries(initialRoots.map((id) => [id, [id]])),
    };
  for (const track of state.undoableState.tracks.filter((track) =>
    targetTracks.has(track.id),
  )) {
    for (const id of track.items) {
      const item = state.undoableState.items[id];
      if (!item) return fail("missing-item");
      if (
        item.from + item.durationInFrames > merged[0]!.start &&
        !editableTree(state, id)
      )
        return fail("locked-or-invalid-group");
    }
  }
  let next = state;
  for (const range of [...merged].reverse()) {
    if (
      !next.undoableState.tracks.some(
        (track) =>
          targetTracks.has(track.id) &&
          track.items.some((id) => {
            const item = next.undoableState.items[id];
            return item && item.from + item.durationInFrames > range.start;
          }),
      )
    )
      continue;
    for (const boundary of [range.start, range.end]) {
      for (const trackId of targetTracks) {
        const track = next.undoableState.tracks.find(
          (track) => track.id === trackId,
        );
        if (!track) return fail("missing-track");
        for (const id of [...track.items]) {
          const item = next.undoableState.items[id];
          if (!item) return fail("missing-item");
          if (
            item.from >= boundary ||
            item.from + item.durationInFrames <= boundary
          )
            continue;
          const beforeRoots = new Set(
            next.undoableState.tracks.find((track) => track.id === trackId)!
              .items,
          );
          const divided = splitItem({
            state: next,
            idToSplit: id,
            framePosition: boundary,
          });
          if (divided === next) return fail("split-unavailable");
          const origin = origins.get(id) ?? [id];
          origins.delete(id);
          for (const root of divided.undoableState.tracks.find(
            (track) => track.id === trackId,
          )!.items)
            if (!beforeRoots.has(root)) origins.set(root, origin);
          next = divided;
        }
      }
    }
    const delta = range.end - range.start;
    const items = { ...next.undoableState.items };
    const removed = new Set<string>(),
      shifted = new Set<string>();
    const removeTree = (id: string) => {
      if (removed.has(id)) return;
      removed.add(id);
      const item = items[id];
      if (item?.type === "group") item.children.forEach(removeTree);
      delete items[id];
    };
    const shiftTree = (id: string) => {
      if (shifted.has(id)) return;
      shifted.add(id);
      const item = items[id];
      if (!item) return;
      if (item.type === "group") item.children.forEach(shiftTree);
      items[id] = { ...item, from: item.from - delta };
    };
    const tracks = next.undoableState.tracks.map((track) => {
      if (!targetTracks.has(track.id)) return track;
      const kept: string[] = [];
      for (const id of track.items) {
        const item = items[id];
        if (!item) continue;
        if (
          item.from >= range.start &&
          item.from + item.durationInFrames <= range.end
        ) {
          removeTree(id);
          continue;
        }
        if (item.from >= range.end) shiftTree(id);
        kept.push(id);
      }
      return { ...track, items: kept };
    });
    const transitions = { ...next.undoableState.transitions };
    const project = (frame: number) =>
      frame <= range.start
        ? frame
        : frame >= range.end
          ? frame - delta
          : range.start;
    for (const [id, transition] of Object.entries(transitions)) {
      if (
        removed.has(transition.sourceItemId) ||
        removed.has(transition.targetItemId)
      ) {
        delete transitions[id];
        continue;
      }
      if (!targetTracks.has(transition.trackId)) continue;
      const from = project(transition.from),
        end = project(transition.from + transition.durationInFrames);
      if (end <= from) delete transitions[id];
      else
        transitions[id] = { ...transition, from, durationInFrames: end - from };
    }
    next = {
      ...next,
      undoableState: { ...next.undoableState, tracks, items, transitions },
    };
  }
  const replacements: Record<string, string[]> = Object.fromEntries(
    initialRoots.map((id) => [id, []]),
  );
  for (const track of next.undoableState.tracks)
    for (const id of track.items)
      for (const origin of origins.get(id) ?? [id])
        replacements[origin]?.push(id);
  const selectedItems = state.selectedItems.flatMap(
    (id) => replacements[id] ?? (next.undoableState.items[id] ? [id] : []),
  );
  if (next === state) return { ok: true, state, replacements };
  return {
    ok: true,
    state: {
      ...next,
      selectedItems: [...new Set(selectedItems)],
      timelineTimeEditApplied: true,
    },
    replacements,
  };
}
